"use client";

import React, { useMemo } from "react";
import { FileText } from "lucide-react";

import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { useContractHooks } from "@/lib/hooks/api/shared/contracts-graphql";
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { Contracts } from "@/src/gql/graphql";

const CONTRACTS_CONFIG = getEntityTableConfig<Contracts>("contracts");

interface DealContractsTabProps {
  dealId: string;
  dealTitle?: string;
  className?: string;
}

/**
 * Contracts linked to this deal. Reuses the universal contracts table
 * (row actions + right-click via the registry's detailActions) filtered by
 * `deal_id`. UniversalEntityPage wraps ColumnManagerProvider →
 * FilterManagerProvider internally (PROVIDER_ORDERING_ENFORCEMENT).
 */
export function DealContractsTab({
  dealId,
  dealTitle,
  className = "",
}: DealContractsTabProps) {
  const contractHooks = useContractHooks();

  const config = useMemo(
    () => ({
      ...CONTRACTS_CONFIG,
      displayName: dealTitle ? `Contracts for ${dealTitle}` : "Contracts",
    }),
    [dealTitle]
  );

  const customFilters = useMemo(() => ({ deal_id: dealId }), [dealId]);

  if (!dealId) {
    return (
      <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-lg border bg-card p-8 text-center">
        <FileText className="h-8 w-8 text-muted-foreground" />
        <h3 className="text-base font-semibold text-card-foreground">No contracts</h3>
        <p className="text-sm text-muted-foreground">
          No contracts have been linked to this deal yet.
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <UniversalEntityPage
        entityName="contracts"
        config={config}
        hooks={contractHooks}
        filterPreset="all"
        title={dealTitle ? `Contracts for ${dealTitle}` : "Contracts"}
        description="Contracts linked to this deal"
        storageKey={`deal-${dealId}-contracts-column-preferences`}
        customFilters={customFilters}
        showHeader={false}
      />
    </div>
  );
}
